import React, { useState, useEffect, useCallback } from 'react';
import http from 'Utils/http';
import { usePolling } from 'Hooks/usePolling';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Tabs,
    Tab,
    Typography,
    TextField,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';

const SAMPLE_RATIOS = [
    { value: 1, label: '100%' },
    { value: 0.5, label: '50%' },
    { value: 0.1, label: '10%' },
    { value: 0.01, label: '1%' },
];

const SOURCE_TABS = [
    { value: 'laravel', label: 'Laravel' },
    { value: 'symfony', label: 'Symfony' },
];

const KIND_OPTIONS = ['server', 'client'];

const STATUS_OPTIONS = ['Ok', 'Error', 'Unset'];

const DEFAULT_RULES = {
    name: '*',
    kind: KIND_OPTIONS,
    method: '*',
    url: '*',
    status: STATUS_OPTIONS,
    http_status: '*',
    min_duration_ms: 0,
};

const TYPE_FILTERS = [
    { value: '', label: 'All types' },
    { value: 2, label: 'Server' },
    { value: 1, label: 'Client' },
    { value: 0, label: 'Internal' },
];

const KIND_LABEL = {
    0: 'Internal',
    1: 'Client',
    2: 'Server',
    3: 'Producer',
    4: 'Consumer',
};

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatTime = (value) => {
    const parsed = new Date(value);

    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const DARK_TEXT = { color: 'primary.blackText' };

const RAW_BLOCK = {
    color: 'primary.blackText',
    backgroundColor: '#F5F7F9',
    fontFamily: 'monospace',
    fontSize: '1.1rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    padding: '10px',
    margin: 0,
    borderRadius: '4px',
};

const RECORDING_DOT = {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: '#D76A6A',
    animation: 'otelPulse 1.4s ease-in-out infinite',
    '@keyframes otelPulse': {
        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.25, transform: 'scale(0.75)' },
    },
};

const START_BUTTON = {
    backgroundColor: '#6AB77E',
    '&:hover': { backgroundColor: '#4E9963' },
    '&:active': { backgroundColor: '#3E7C4F' },
};

const CHECKBOX_LABEL = { '& .MuiFormControlLabel-label': DARK_TEXT };

const STATUS_COLOR = {
    Ok: 'success',
    Error: 'error',
    Unset: 'default',
};

const SpanRow = ({ record }) => {
    const [open, setOpen] = useState(false);
    const attributes = record.attributes || {};

    return (
        <>
            <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
                <TableCell>{formatTime(record.time)}</TableCell>
                <TableCell>{record.name}</TableCell>
                <TableCell align="center">{KIND_LABEL[record.kind] ?? record.kind}</TableCell>
                <TableCell align="center">
                    {record.duration_ms === null || record.duration_ms === undefined
                        ? '-'
                        : `${record.duration_ms} ms`}
                </TableCell>
                <TableCell align="center">
                    <Chip
                        size="small"
                        label={record.status || '-'}
                        color={STATUS_COLOR[record.status] || 'default'}
                    />
                </TableCell>
                <TableCell align="center">{attributes['http.response.status_code'] ?? '-'}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: '10px 0' }}>
                            <Typography variant="subtitle2" sx={DARK_TEXT}>Trace</Typography>
                            <Typography sx={{ ...DARK_TEXT, fontFamily: 'monospace', marginBottom: '10px' }}>
                                {record.trace_id || '-'}
                            </Typography>
                            <Typography variant="subtitle2" sx={DARK_TEXT}>Span</Typography>
                            <Typography sx={{ ...DARK_TEXT, fontFamily: 'monospace', marginBottom: '10px' }}>
                                {record.span_id || '-'}
                            </Typography>
                            <Typography variant="subtitle2" sx={DARK_TEXT}>Parent span</Typography>
                            <Typography sx={{ ...DARK_TEXT, fontFamily: 'monospace', marginBottom: '10px' }}>
                                {record.parent_span_id || '-'}
                            </Typography>
                            {record.status_description && (
                                <>
                                    <Typography variant="subtitle2" sx={DARK_TEXT}>Status description</Typography>
                                    <Typography sx={{ ...DARK_TEXT, marginBottom: '10px' }}>
                                        {record.status_description}
                                    </Typography>
                                </>
                            )}
                            <Typography variant="subtitle2" sx={DARK_TEXT}>Attributes</Typography>
                            <Box component="pre" sx={RAW_BLOCK}>
                                {JSON.stringify(attributes, null, 2)}
                            </Box>
                            <Typography variant="subtitle2" sx={{ ...DARK_TEXT, marginTop: '15px' }}>Raw record</Typography>
                            <Box component="pre" sx={RAW_BLOCK}>
                                {record.raw || '-'}
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const RulesDialog = ({ open, rules, onChange, sampleRatio, onSampleRatioChange, onCancel, onConfirm, confirmLabel }) => {
    const toggleInList = (field, value) => {
        const list = rules[field] || [];
        const next = list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
        onChange({ ...rules, [field]: next });
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle sx={DARK_TEXT}>What to record</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ ...DARK_TEXT, marginBottom: '15px' }}>
                    A span is written only when it matches every condition below. Text fields accept
                    wildcards, `*` means any value.
                </Typography>

                <Typography variant="subtitle2" sx={DARK_TEXT}>Kind</Typography>
                <FormGroup row sx={{ marginBottom: '10px' }}>
                    {KIND_OPTIONS.map((kind) => (
                        <FormControlLabel
                            key={kind}
                            control={
                                <Checkbox
                                    checked={(rules.kind || []).includes(kind)}
                                    onChange={() => toggleInList('kind', kind)}
                                />
                            }
                            label={kind}
                            sx={CHECKBOX_LABEL}
                        />
                    ))}
                </FormGroup>

                <Typography variant="subtitle2" sx={DARK_TEXT}>Status</Typography>
                <FormGroup row sx={{ marginBottom: '15px' }}>
                    {STATUS_OPTIONS.map((status) => (
                        <FormControlLabel
                            key={status}
                            control={
                                <Checkbox
                                    checked={(rules.status || []).includes(status)}
                                    onChange={() => toggleInList('status', status)}
                                />
                            }
                            label={status}
                            sx={CHECKBOX_LABEL}
                        />
                    ))}
                </FormGroup>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <TextField
                        size="small"
                        label="Span name"
                        helperText="For example: GET /api/*"
                        value={rules.name ?? '*'}
                        onChange={(e) => onChange({ ...rules, name: e.target.value })}
                    />
                    <TextField
                        size="small"
                        label="HTTP method"
                        helperText="For example: POST"
                        value={rules.method ?? '*'}
                        onChange={(e) => onChange({ ...rules, method: e.target.value })}
                    />
                    <TextField
                        size="small"
                        label="URL path"
                        helperText="For example: api/user/flow/*"
                        value={rules.url ?? '*'}
                        onChange={(e) => onChange({ ...rules, url: e.target.value })}
                    />
                    <TextField
                        size="small"
                        label="HTTP status"
                        helperText="For example: 5* keeps server errors only"
                        value={rules.http_status ?? '*'}
                        onChange={(e) => onChange({ ...rules, http_status: e.target.value })}
                    />
                    <TextField
                        size="small"
                        type="number"
                        label="Minimum duration, ms"
                        helperText="Faster spans are skipped"
                        value={rules.min_duration_ms ?? 0}
                        onChange={(e) => onChange({ ...rules, min_duration_ms: e.target.value })}
                    />
                    <TextField
                        select
                        size="small"
                        label="Sampling"
                        helperText="Share of traces recorded, whole traces are kept or dropped"
                        value={sampleRatio}
                        onChange={(e) => onSampleRatioChange(e.target.value)}
                    >
                        {SAMPLE_RATIOS.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" onClick={onConfirm}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TelemetryPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [hasMore, setHasMore] = useState(false);
    const [records, setRecords] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [search, setSearch] = useState('');
    const [source, setSource] = useState('laravel');
    const [kindFilter, setKindFilter] = useState('');

    const [settingsBySource, setSettingsBySource] = useState({});

    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftRules, setDraftRules] = useState(DEFAULT_RULES);
    const [draftRatio, setDraftRatio] = useState(1);
    const [startAfterSave, setStartAfterSave] = useState(false);
    const [now, setNow] = useState(new Date());

    const current = settingsBySource[source];
    const known = current !== undefined;
    const enabled = Boolean(current?.enabled);
    const sampleRatio = Number(current?.sample_ratio ?? 1);
    const rules = { ...DEFAULT_RULES, ...(current?.rules || {}) };

    const applySettings = (target, settings) => {
        if (!settings) {
            return;
        }

        setSettingsBySource((prev) => ({ ...prev, [target]: settings }));
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSourceTab = (event, value) => {
        setSource(value);
        setPage(0);
        setRecords([]);
        setHasMore(false);
    };

    const fetchData = useCallback((showLoader = true) => {
        const target = source;

        if (showLoader) {
            dispatch(setLoading(true));
        }

        const pagination = encodeURIComponent(
            JSON.stringify({ pageSize: rowsPerPage, currentPage: page })
        );
        const dateFromParam = dateFrom ? `&date_from=${dateFrom}` : '';
        const dateToParam = dateTo ? `&date_to=${dateTo}` : '';
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const kindParam = kindFilter === '' ? '' : `&kind=${kindFilter}`;
        const url = `/admin/otel/spans?source=${source}&pagination=${pagination}${dateFromParam}${dateToParam}${searchParam}${kindParam}`;

        http.get(url)
            .then((res) => {
                setRecords(res.data.records || []);
                setHasMore(Boolean(res.data.pagination?.hasMore));
                applySettings(target, res.data.settings);
                if (showLoader) {
                    dispatch(setLoading(false));
                }
            })
            .catch((err) => {
                if (showLoader) {
                    dispatch(setLoading(false));
                }
                enqueueSnackbar(`${err.response?.data?.error || 'Error loading telemetry'}`, {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            });
    }, [dispatch, enqueueSnackbar, page, rowsPerPage, dateFrom, dateTo, search, source, kindFilter]);

    const saveSettings = (payload, message) =>
        http.post('/admin/otel/settings', { ...payload, source })
            .then((res) => {
                applySettings(source, res.data.settings);
                enqueueSnackbar(message, { action: CustomSnackbar, variant: 'success' });
            })
            .catch((err) => {
                enqueueSnackbar(`${err.response?.data?.error || 'Unable to change telemetry settings'}`, {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            });

    const handleStartStop = () => {
        if (enabled) {
            saveSettings({ enabled: false }, 'Telemetry stopped');
            return;
        }

        setDraftRules(rules);
        setDraftRatio(sampleRatio);
        setStartAfterSave(true);
        setDialogOpen(true);
    };

    const openRules = () => {
        setDraftRules(rules);
        setDraftRatio(sampleRatio);
        setStartAfterSave(false);
        setDialogOpen(true);
    };

    const confirmRules = () => {
        setDialogOpen(false);

        const payload = { rules: draftRules, sample_ratio: draftRatio };
        if (startAfterSave) {
            payload.enabled = true;
        }

        saveSettings(payload, startAfterSave ? 'Telemetry started' : 'Recording rules saved');
    };

    useEffect(() => {
        fetchData(true);
    }, [fetchData]);

    usePolling(() => fetchData(false), 7000, enabled);

    useEffect(() => {
        const intervalId = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(intervalId);
    }, []);

    return (
        <>
            <RulesDialog
                open={dialogOpen}
                rules={draftRules}
                onChange={setDraftRules}
                sampleRatio={draftRatio}
                onSampleRatioChange={setDraftRatio}
                onCancel={() => setDialogOpen(false)}
                onConfirm={confirmRules}
                confirmLabel={startAfterSave ? 'Start' : 'Save'}
            />
            <Box sx={{ position: 'relative', marginBottom: '25px' }}>
                <Typography
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '1.2rem',
                    }}
                >
                    {`${now.toLocaleString()} ${TIME_ZONE}`}
                </Typography>
                <Typography
                    variant="h1"
                    sx={{
                        textAlign: 'center',
                        fontSize: '3rem',
                        fontWeight: 500,
                    }}
                >
                    Telemetry
                </Typography>
            </Box>
            <Box component={Paper} sx={{ padding: '20px 20px 0px 20px', overflowX: 'auto' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                        minWidth: 'max-content',
                    }}
                >
                    <Tabs value={source} onChange={handleSourceTab}>
                        {SOURCE_TABS.map((tab) => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {enabled && <Box sx={RECORDING_DOT} />}
                        <Button
                            variant="contained"
                            color={enabled ? 'secondary' : 'primary'}
                            startIcon={enabled ? <StopIcon /> : <PlayArrowIcon />}
                            onClick={handleStartStop}
                            disabled={!known}
                            sx={enabled ? undefined : START_BUTTON}
                        >
                            {enabled ? 'Stop' : 'Start'}
                        </Button>
                        <Button color="primary" variant="outlined" onClick={openRules} disabled={!known}>
                            Rules
                        </Button>
                        <TextField
                            select
                            size="small"
                            label="Type"
                            value={kindFilter}
                            onChange={(e) => { setKindFilter(e.target.value); setPage(0); }}
                            sx={{ minWidth: 130 }}
                        >
                            {TYPE_FILTERS.map((item) => (
                                <MenuItem key={item.label} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            label="Search"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            sx={{ minWidth: 220 }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                        />
                        <Button color="primary" variant="outlined" onClick={() => fetchData(true)}>
                            Refresh
                        </Button>
                    </Box>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 650 }} size="medium">
                        <TableHead>
                            <TableRow
                                sx={{
                                    height: '50px',
                                    '& .MuiTableCell-root': {
                                        fontSize: '1.2rem',
                                    },
                                }}
                            >
                                <TableCell>Time</TableCell>
                                <TableCell>Span</TableCell>
                                <TableCell align="center">Type</TableCell>
                                <TableCell align="center">Duration</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">HTTP</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No spans found
                                    </TableCell>
                                </TableRow>
                            )}
                            {records.map((record) => (
                                <SpanRow key={`${record.trace_id}-${record.span_id}-${record.time}`} record={record} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography variant="body2" sx={DARK_TEXT}>Rows per page:</Typography>
                        <TextField
                            select
                            size="small"
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                            sx={{ minWidth: 80 }}
                        >
                            {[25, 50, 100].map((size) => (
                                <MenuItem key={size} value={size}>
                                    {size}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            color="primary"
                            variant="outlined"
                            disabled={page === 0}
                            onClick={() => setPage(0)}
                            startIcon={<FirstPageIcon />}
                        >
                            Latest
                        </Button>
                        <Button
                            color="primary"
                            variant="outlined"
                            disabled={page === 0}
                            onClick={() => setPage(page - 1)}
                            startIcon={<ChevronLeftIcon />}
                        >
                            Newer
                        </Button>
                        <Typography variant="body2" sx={DARK_TEXT}>Page {page + 1}</Typography>
                        <Button
                            color="primary"
                            variant="outlined"
                            disabled={!hasMore}
                            onClick={() => setPage(page + 1)}
                            endIcon={<ChevronRightIcon />}
                        >
                            Older
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default TelemetryPage;
